import '@/styles/dropdown.css'
import { RootState } from "@/redux"
import { useSelector } from "react-redux"
import { Checkbox } from "@/components/ui/checkbox"
import { Accordion, AccordionContent, AccordionItem, AccordionSubTrigger, AccordionTrigger } from "@/components/ui/accordion"
import { getFormattedDisplayName } from '../helper/getFormattedDisplayName'
import { FormLabel } from '@/components/ui/form'

export const Fanout = ({
  iterationEnabled,
  setIterationEnabled,
  selectedSubject,
  addOrRemoveSubject,
  iterableFields,
  actionDisplayName,
  subjectDisplayName,
}: {
  iterationEnabled: boolean,
  setIterationEnabled: (value: boolean) => void,
  selectedSubject?: string,
  addOrRemoveSubject: (subject: string) => void,
  iterableFields: Record<string, unknown>,
  actionDisplayName: string,
  subjectDisplayName: string,
}) => {
  return (
    <div className="flex items-start w-full mb-4">
      <div className="flex items-center space-x-2 mr-4 mt-2 w-1/2">
        <Checkbox
          checked={iterationEnabled}
          onCheckedChange={() => setIterationEnabled(!iterationEnabled)}
        />
        <FormLabel className="font-semibold text-primary !text-sm">
          {actionDisplayName} {subjectDisplayName} for every
        </FormLabel>
        {iterationEnabled ? (
          <div className='px-2 border text-primary w-[290px] ml-4 bg-foreground'>
            <Accordion
              type="single"
              collapsible
              disabled={!iterationEnabled}
            >
              <AccordionItem value="selectedSubjects" className="rounded-md">
                <AccordionTrigger className="flex justify-between text-sm h-10">
                  {selectedSubject ? selectedSubject : 'Select'}
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <NestedAccordion
                    data={iterableFields}
                    selectedSubject={selectedSubject}
                    addOrRemoveSubject={addOrRemoveSubject}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        ) : (
          <div className='px-2 h-10 w-[290px] text-sm flex items-center border text-secondary ml-4 bg-muted-foreground'>Select</div>
        )}
      </div>
    </div>
  )
}

const NestedAccordion = ({
  data,
  selectedSubject,
  addOrRemoveSubject,
}: {
  data: Record<string, unknown>,
  selectedSubject: string | undefined,
  addOrRemoveSubject: (subject: string) => void,
}) => {
  const { applications } = useSelector((state: RootState) => state.application)
  const { steps } = useSelector((state: RootState) => state.workflow.create)

  return (
    <div className="ml-2">
      {Object.entries(data).map(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          return (
            <Accordion
              type="single"
              collapsible
              key={key + value}
            >
              <AccordionItem value={JSON.stringify(value)}>
                <AccordionSubTrigger className="flex justify-start">
                  <div className="flex justify-start">
                    {getFormattedDisplayName(key, applications, steps)}
                  </div>
                </AccordionSubTrigger>
                <AccordionContent className="space-y-2">
                  <NestedAccordion
                    data={value as Record<string, unknown>}
                    selectedSubject={selectedSubject}
                    addOrRemoveSubject={addOrRemoveSubject}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )
        } else {
          return (
            <div className="flex items-center space-x-2" key={key}>
              <Checkbox
                id={key.toLowerCase()}
                checked={selectedSubject === value}
                onCheckedChange={() => addOrRemoveSubject(value as string)}
              />
              <label
                className={`cursor-pointer py-1 ${selectedSubject === value ? 'text-accent font-semibold' : ''}`}
                htmlFor={key.toLowerCase()}
              >
                {key}
              </label>
            </div>
          )
        }
      })}
    </div>
  )
}
